import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Languages,
  Search,
  Save,
  Plus,
  Trash2,
  Edit3,
  Globe,
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

export default function TranslationManagementModule() {
  const { t, i18n } = useTranslation(["common", "dashboard", "admin"]);

  const [languages, setLanguages] = useState([]);
  const [namespaces, setNamespaces] = useState([]);
  const [selectedLang, setSelectedLang] = useState("tr");
  const [selectedNs, setSelectedNs] = useState("common");
  const [translations, setTranslations] = useState({});
  const [originalTranslations, setOriginalTranslations] = useState({});
  const [compareLang, setCompareLang] = useState("en");
  const [compareTranslations, setCompareTranslations] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAddLangModal, setShowAddLangModal] = useState(false);
  const [showAddKeyModal, setShowAddKeyModal] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [newLang, setNewLang] = useState({ code: "", name: "", copyFrom: "tr" });
  const [newKey, setNewKey] = useState({ key: "", value: "" });

  const authFetch = async (url, options = {}) => {
    // Cookie-based auth (credentials: 'include') matches the rest of the app.
    // Still forward a Bearer token if present (local dev fallback).
    const token = localStorage.getItem("token");
    return fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });
  };

  const fetchLanguages = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/translations/languages`);
      if (res.ok) {
        const data = await res.json();
        setLanguages(data.languages);
        // Sort namespaces: common first, then alphabetically
        const preferred = ["common", "auth", "landing", "dashboard", "memberDashboard", "reservations", "members", "trainers", "settings", "payments", "rooms", "messaging", "business"];
        const sorted = (data.namespaces || []).sort((a, b) => {
          const ai = preferred.indexOf(a);
          const bi = preferred.indexOf(b);
          return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
        });
        setNamespaces(sorted);
      }
    } catch (err) {
      console.error("Error fetching languages:", err);
    }
  }, []);

  const fetchTranslations = useCallback(async (lang, ns) => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/translations/languages/${lang}/${ns}`);
      if (res.ok) {
        const data = await res.json();
        setTranslations(data.translations);
        setOriginalTranslations(JSON.parse(JSON.stringify(data.translations)));
        setHasChanges(false);
      }
    } catch (err) {
      console.error("Error fetching translations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompareTranslations = useCallback(async (lang, ns) => {
    try {
      const res = await authFetch(`${API}/translations/languages/${lang}/${ns}`);
      if (res.ok) {
        const data = await res.json();
        setCompareTranslations(data.translations);
      }
    } catch (err) {
      console.error("Error fetching compare translations:", err);
    }
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  useEffect(() => {
    if (selectedLang && selectedNs) {
      fetchTranslations(selectedLang, selectedNs);
    }
  }, [selectedLang, selectedNs, fetchTranslations]);

  useEffect(() => {
    if (compareLang && selectedNs && compareLang !== selectedLang) {
      fetchCompareTranslations(compareLang, selectedNs);
    }
  }, [compareLang, selectedNs, selectedLang, fetchCompareTranslations]);

  const flattenObject = (obj, prefix = "") => {
    const result = {};
    for (const key in obj) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === "object" && obj[key] !== null && !Array.isArray(obj[key])) {
        Object.assign(result, flattenObject(obj[key], fullKey));
      } else {
        result[fullKey] = obj[key];
      }
    }
    return result;
  };

  const setNestedValue = (obj, path, value) => {
    const keys = path.split(".");
    const newObj = JSON.parse(JSON.stringify(obj));
    let current = newObj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]] || typeof current[keys[i]] !== "object") {
        current[keys[i]] = {};
      }
      current = current[keys[i]];
    }
    current[keys[keys.length - 1]] = value;
    return newObj;
  };

  const deleteNestedKey = (obj, path) => {
    const keys = path.split(".");
    const newObj = JSON.parse(JSON.stringify(obj));
    let current = newObj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (!current[keys[i]]) return newObj;
      current = current[keys[i]];
    }
    delete current[keys[keys.length - 1]];
    return newObj;
  };

  const getNestedValue = (obj, path) => {
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
      if (!current || typeof current !== "object") return undefined;
      current = current[key];
    }
    return current;
  };

  const handleValueChange = (key, value) => {
    const updated = setNestedValue(translations, key, value);
    setTranslations(updated);
    setHasChanges(true);
  };

  const handleDeleteKey = (key) => {
    const updated = deleteNestedKey(translations, key);
    setTranslations(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await authFetch(
        `${API}/translations/languages/${selectedLang}/${selectedNs}`,
        {
          method: "PUT",
          body: JSON.stringify({ translations }),
        }
      );
      if (res.ok) {
        toast.success(`${selectedLang}/${selectedNs} kaydedildi`);
        setOriginalTranslations(JSON.parse(JSON.stringify(translations)));
        setHasChanges(false);
        i18n.reloadResources(selectedLang, selectedNs);
      } else {
        toast.error(t('common:saveError'));
      }
    } catch (err) {
      toast.error(t('common:saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddLanguage = async () => {
    if (!newLang.code || !newLang.name) {
      toast.error(t("admin:langCodeAndNameRequired"));
      return;
    }
    try {
      const res = await authFetch(`${API}/translations/languages`, {
        method: "POST",
        body: JSON.stringify({
          language_code: newLang.code,
          language_name: newLang.name,
          copy_from: newLang.copyFrom,
        }),
      });
      if (res.ok) {
        toast.success(`Yeni dil eklendi: ${newLang.code}`);
        setShowAddLangModal(false);
        setNewLang({ code: "", name: "", copyFrom: "tr" });
        fetchLanguages();
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common:langAddFailed"));
      }
    } catch (err) {
      toast.error(t("common:langAddFailed"));
    }
  };

  const handleDeleteLanguage = async (lang) => {
    if (!window.confirm(t("common:confirmDeleteLanguage", { lang }))) return;
    try {
      const res = await authFetch(`${API}/translations/languages/${lang}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success(t("common:langDeleted", { lang }));
        fetchLanguages();
        if (selectedLang === lang) setSelectedLang("tr");
      } else {
        const err = await res.json();
        toast.error(err.detail || t("common:langDeleteFailed"));
      }
    } catch (err) {
      toast.error(t("common:langDeleteFailed"));
    }
  };

  const handleAddKey = () => {
    if (!newKey.key || !newKey.value) {
      toast.error(t("common:keyAndValueRequired"));
      return;
    }
    const updated = setNestedValue(translations, newKey.key, newKey.value);
    setTranslations(updated);
    setHasChanges(true);
    setShowAddKeyModal(false);
    setNewKey({ key: "", value: "" });
    toast.success(t("common:keyAdded"));
  };

  const flatEntries = flattenObject(translations);
  const flatCompare = flattenObject(compareTranslations);
  const filteredKeys = Object.keys(flatEntries)
    .filter((key) => {
      if (!searchTerm) return true;
      const lower = searchTerm.toLowerCase();
      return (
        key.toLowerCase().includes(lower) ||
        String(flatEntries[key]).toLowerCase().includes(lower)
      );
    })
    .sort();

  const groups = {};
  filteredKeys.forEach((key) => {
    const parts = key.split(".");
    const group = parts.length > 1 ? parts[0] : "_root";
    if (!groups[group]) groups[group] = [];
    groups[group].push(key);
  });

  const toggleGroup = (group) => {
    setExpandedGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  const NAMESPACE_LABELS = {
    common: "Genel",
    landing: "Ana Sayfa",
    auth: "Giriş/Kayıt",
    dashboard: "Panel",
    memberDashboard: "Üye Paneli",
    reservations: "Rezervasyonlar",
    members: "Üyeler",
    trainers: "Antrenörler",
    settings: "Ayarlar",
    payments: "Ödemeler",
    rooms: "Odalar",
    messaging: "Mesajlaşma",
    business: "İşletme",
    packages: "Paketler",
    reports: "Raporlar",
    events: "Etkinlikler",
    employees: "Çalışanlar",
    surveys: "Anketler/Formlar",
    admin: "Admin",
    qr: "QR Giriş",
    evaluations: "Değerlendirmeler",
  };

  const LANG_LABELS = {
    tr: "Turkce",
    en: "English",
    de: "Deutsch",
    ar: "Arabic",
    ru: "Russian",
    fr: "French",
    es: "Spanish",
  };

  return (
    <div className="space-y-6" data-testid="translation-management">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-bold text-xl text-navy flex items-center gap-2">
            <Languages className="w-6 h-6" />
            Dil Yonetimi
          </h2>
          <p className="text-sm text-grey mt-1">
            Ceviri dosyalarini goruntuleyip duzenleyin, yeni dil ekleyin
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddLangModal(true)}
            data-testid="add-language-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Yeni Dil
          </Button>
          {hasChanges && (
            <Button
              size="sm"
              onClick={handleSave}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              data-testid="save-translations-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Kaydet
            </Button>
          )}
        </div>
      </div>

      {/* Language Cards */}
      <div className="flex flex-wrap gap-3">
        {languages.map((lang) => (
          <div
            key={lang}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
              selectedLang === lang
                ? "border-navy bg-navy/5 text-navy font-medium"
                : "border-slate-200 hover:border-slate-300"
            }`}
            onClick={() => setSelectedLang(lang)}
            data-testid={`lang-card-${lang}`}
          >
            <Globe className="w-4 h-4" />
            <span className="uppercase font-mono text-sm">{lang}</span>
            <span className="text-xs text-grey">{LANG_LABELS[lang] || lang}</span>
            {lang !== "tr" && lang !== "en" && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteLanguage(lang);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDeleteLanguage(lang);
                }}
                className="ml-1 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                data-testid={`delete-lang-${lang}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Namespace Tabs + Compare Language */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-wrap gap-1">
          {namespaces.map((ns) => (
            <button
              key={ns}
              onClick={() => setSelectedNs(ns)}
              className={`px-3 py-1.5 text-xs rounded-md font-medium transition-all ${
                selectedNs === ns
                  ? "bg-navy text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              data-testid={`ns-tab-${ns}`}
            >
              {NAMESPACE_LABELS[ns] || ns}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-grey">{t("common:compare")}</span>
          <Select value={compareLang} onValueChange={setCompareLang}>
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages
                .filter((l) => l !== selectedLang)
                .map((l) => (
                  <SelectItem key={l} value={l}>
                    {l.toUpperCase()}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Search + Add Key */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey" />
          <Input
            placeholder={t("common:searchKeyOrValue")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-translations"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAddKeyModal(true)}
          data-testid="add-key-btn"
        >
          <Plus className="w-4 h-4 mr-1" />
          Yeni Anahtar
        </Button>
      </div>

      {/* Translation Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-navy" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="max-h-[600px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    <TableHead className="w-[280px]">{t("common:key")}</TableHead>
                    <TableHead>
                      {selectedLang.toUpperCase()} Degeri
                    </TableHead>
                    {compareLang !== selectedLang && (
                      <TableHead className="text-grey">
                        {compareLang.toUpperCase()} Degeri
                      </TableHead>
                    )}
                    <TableHead className="w-[80px] text-right">{t("common:actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(groups).map(([group, keys]) => (
                    <GroupRows
                      key={group}
                      group={group}
                      keys={keys}
                      flatEntries={flatEntries}
                      flatCompare={flatCompare}
                      compareLang={compareLang}
                      selectedLang={selectedLang}
                      expanded={expandedGroups[group] !== false}
                      onToggle={() => toggleGroup(group)}
                      onValueChange={handleValueChange}
                      onDelete={handleDeleteKey}
                    />
                  ))}
                  {filteredKeys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-10 text-grey">
                        {searchTerm ? t("common:noSearchResults") : t("common:noTranslationsInNs")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="text-xs text-grey text-right">
        {filteredKeys.length} anahtar {hasChanges && " | Kaydedilmemis degisiklikler var"}
      </div>

      {/* Add Language Modal */}
      <Dialog open={showAddLangModal} onOpenChange={setShowAddLangModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:addNewLanguage")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("admin:langCode")}</Label>
              <Input
                placeholder="orn: de, fr, ar, es"
                value={newLang.code}
                onChange={(e) => setNewLang({ ...newLang, code: e.target.value })}
                data-testid="new-lang-code"
              />
              <p className="text-xs text-grey mt-1">ISO 639-1 kodu (2 karakter)</p>
            </div>
            <div>
              <Label>{t("common:langName")}</Label>
              <Input
                placeholder="orn: Deutsch, Francais, Arabic"
                value={newLang.name}
                onChange={(e) => setNewLang({ ...newLang, name: e.target.value })}
                data-testid="new-lang-name"
              />
            </div>
            <div>
              <Label>{t("common:copyFromLang")}</Label>
              <Select
                value={newLang.copyFrom}
                onValueChange={(v) => setNewLang({ ...newLang, copyFrom: v })}
              >
                <SelectTrigger data-testid="new-lang-copy-from">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l.toUpperCase()} - {LANG_LABELS[l] || l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-grey mt-1">
                Mevcut ceviriler bu dilden kopyalanir, sonra duzenleyebilirsiniz
              </p>
            </div>
            <Button onClick={handleAddLanguage} className="w-full" data-testid="confirm-add-lang">
              <Plus className="w-4 h-4 mr-1" />
              Dil Ekle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Key Modal */}
      <Dialog open={showAddKeyModal} onOpenChange={setShowAddKeyModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("common:newTranslationKey")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>{t("common:key")}</Label>
              <Input
                placeholder="orn: newFeature veya group.subKey"
                value={newKey.key}
                onChange={(e) => setNewKey({ ...newKey, key: e.target.value })}
                data-testid="new-key-name"
              />
              <p className="text-xs text-grey mt-1">
                Nokta ile alt grup olusturabilirsiniz (orn: sidebar.newItem)
              </p>
            </div>
            <div>
              <Label>Deger ({selectedLang.toUpperCase()})</Label>
              <Input
                placeholder={t("common:translationText")}
                value={newKey.value}
                onChange={(e) => setNewKey({ ...newKey, value: e.target.value })}
                data-testid="new-key-value"
              />
            </div>
            <Button onClick={handleAddKey} className="w-full" data-testid="confirm-add-key">
              <Plus className="w-4 h-4 mr-1" />
              Ekle
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GroupRows({
  group,
  keys,
  flatEntries,
  flatCompare,
  compareLang,
  selectedLang,
  expanded,
  onToggle,
  onValueChange,
  onDelete,
}) {
  if (group === "_root") {
    return keys.map((key) => (
      <TranslationRow
        key={key}
        keyName={key}
        value={flatEntries[key]}
        compareValue={flatCompare[key]}
        compareLang={compareLang}
        selectedLang={selectedLang}
        onValueChange={onValueChange}
        onDelete={onDelete}
      />
    ));
  }

  return (
    <>
      <TableRow
        className="bg-slate-50 cursor-pointer hover:bg-slate-100"
        onClick={onToggle}
      >
        <TableCell colSpan={4} className="py-2">
          <div className="flex items-center gap-2 font-medium text-sm text-navy">
            {expanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
            {group}
            <Badge variant="secondary" className="text-xs">
              {keys.length}
            </Badge>
          </div>
        </TableCell>
      </TableRow>
      {expanded &&
        keys.map((key) => (
          <TranslationRow
            key={key}
            keyName={key}
            value={flatEntries[key]}
            compareValue={flatCompare[key]}
            compareLang={compareLang}
            selectedLang={selectedLang}
            onValueChange={onValueChange}
            onDelete={onDelete}
          />
        ))}
    </>
  );
}

function TranslationRow({
  keyName,
  value,
  compareValue,
  compareLang,
  selectedLang,
  onValueChange,
  onDelete,
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const displayKey = keyName.includes(".") ? keyName.split(".").slice(1).join(".") : keyName;
  const isMissing = compareValue === undefined;

  return (
    <TableRow className={editing ? "bg-blue-50/50" : ""}>
      <TableCell className="font-mono text-xs text-slate-600 align-top py-2">
        <span title={keyName}>{displayKey}</span>
        {isMissing && compareLang !== selectedLang && (
          <AlertTriangle className="inline w-3 h-3 ml-1 text-amber-500" title={`${compareLang} cevirisi eksik`} />
        )}
      </TableCell>
      <TableCell className="py-2">
        {editing ? (
          <div className="flex gap-2">
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="text-sm h-8"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onValueChange(keyName, editValue);
                  setEditing(false);
                }
                if (e.key === "Escape") {
                  setEditValue(value);
                  setEditing(false);
                }
              }}
            />
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-green-600"
              onClick={() => {
                onValueChange(keyName, editValue);
                setEditing(false);
              }}
            >
              <Check className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div
            className="text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded min-h-[28px] flex items-center"
            onClick={() => {
              setEditValue(value);
              setEditing(true);
            }}
            data-testid={`edit-value-${keyName}`}
          >
            {value || <span className="text-grey italic">bos</span>}
          </div>
        )}
      </TableCell>
      {compareLang !== selectedLang && (
        <TableCell className="text-sm text-grey py-2 align-top">
          {compareValue || <span className="italic text-amber-500">eksik</span>}
        </TableCell>
      )}
      <TableCell className="text-right py-2">
        <button
          onClick={() => onDelete(keyName)}
          className="text-red-400 hover:text-red-600 p-1"
          title="Anahtari sil"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </TableCell>
    </TableRow>
  );
}
